import React, { useEffect, useRef, useState } from "react";
import classes from "./HeaderComponent.module.css";
import previewVideo from "./preview.mp4";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GoogleG, GoogleWordmark } from "../GoogleBrand/GoogleBrand";
import { SocialLinks } from "../SocialLinks/SocialLinks";
import { QuickMessageLinks } from "../QuickMessageLinks/QuickMessageLinks";
import { trackBookClick, trackPricesClick } from "../../analytics";

const BRAND_NAME = "BEST GROOM STUDIO";
const PREVIEW_POSTER = "/preview-poster.jpg";

function HeaderComponent(): JSX.Element {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const [loadVideo, setLoadVideo] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncMotion = (): void => {
      setReduceMotion(mediaQuery.matches);
    };
    syncMotion();
    mediaQuery.addEventListener("change", syncMotion);
    return () => {
      mediaQuery.removeEventListener("change", syncMotion);
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) return;

    const node = mediaRef.current;
    if (node == null) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setLoadVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [reduceMotion]);

  useEffect(() => {
    const video = videoRef.current;
    if (video == null || !loadVideo || reduceMotion) return;

    void video.play().catch(() => {
      /* autoplay may be blocked */
    });
  }, [loadVideo, reduceMotion]);

  return (
    <header className={classes.hero}>
      <div className={classes.container}>
        <div className={classes.layout}>
          <div className={classes.content}>
            <h1 className={classes.brand}>{BRAND_NAME}</h1>
            <p className={classes.headline}>
              {t("header.petGrooming")}{" "}
              <span className={classes.withHeart}>{t("header.withHeart")}</span>
            </p>
            <p className={classes.support}>{t("header.bringingBest")}</p>

            <div className={classes.actions}>
              <a
                className={classes.secondaryButton}
                href="#pricesId"
                onClick={() => {
                  trackPricesClick("hero");
                }}
              >
                {t("prices")}
              </a>
              <Link
                className={classes.primaryButton}
                to="/appointments"
                onClick={() => {
                  trackBookClick("hero");
                }}
              >
                {t("header.bookAppointment")}
              </Link>
            </div>

            <QuickMessageLinks place="hero" className={classes.quickContact} />

            <div className={classes.metaRow}>
              <a
                className={classes.socialProof}
                href="#reviewsId"
                aria-label="Rated 4.7 out of 5 on Google"
              >
                <span className={classes.stars} aria-hidden="true">
                  <span
                    className={classes.starsFill}
                    style={{ width: `${(4.7 / 5) * 100}%` }}
                  >
                    ★★★★★
                  </span>
                  ★★★★★
                </span>
                <span>
                  <strong>4.7</strong> · 225+ {t("header.googleReviews")}
                </span>
                <span className={classes.googleMark}>
                  <GoogleG size={14} />
                  <GoogleWordmark />
                </span>
              </a>

              <SocialLinks
                className={classes.socialLinks}
                linkClassName={classes.socialLink}
                iconSize={18}
              />
            </div>
          </div>

          <div className={classes.media} ref={mediaRef}>
            <div className={classes.mediaGlow} aria-hidden="true" />
            <div className={classes.mediaFrame}>
              {loadVideo && !reduceMotion ? (
                <video
                  ref={videoRef}
                  muted
                  loop
                  className={classes.video}
                  poster={PREVIEW_POSTER}
                  preload="metadata"
                  playsInline
                  autoPlay
                >
                  <source src={previewVideo} type="video/mp4" />
                </video>
              ) : (
                <img
                  src={PREVIEW_POSTER}
                  alt=""
                  className={classes.video}
                  width={420}
                  height={420}
                  decoding="async"
                  fetchPriority="high"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default HeaderComponent;
